import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight, Building2 } from "lucide-react";
import type { DashboardTopSupplier } from "@fiscalo/shared-types";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

/** Just the number — the currency code is rendered separately so the amounts
 *  line up in a column instead of being pushed around by the code's width. */
function formatAmount(n: number, locale: string): string {
  return n.toLocaleString(locale === "es" ? "es-UY" : "en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * The two currencies are PEERS here, deliberately. The backend reports UYU and
 * USD side by side without FX-converting them (dashboard.service.ts), so there
 * is no exchange rate available to rank one above the other — and a supplier
 * billed only in USD would otherwise have their sole figure rendered as a
 * muted footnote while a UYU-only supplier got the full-weight treatment.
 * Style by ROLE (an amount), never by which currency it happens to be.
 */
export function supplierAmounts(
  supplier: DashboardTopSupplier,
): Array<{ currency: string; value: number }> {
  const amounts: Array<{ currency: string; value: number }> = [];
  if (supplier.amountUyu !== 0) {
    amounts.push({ currency: "UYU", value: supplier.amountUyu });
  }
  if (supplier.amountUsd !== 0) {
    amounts.push({ currency: "USD", value: supplier.amountUsd });
  }
  return amounts;
}

/**
 * Top 5 suppliers by spend so far this year. Hidden when there's no activity
 * yet so the dashboard doesn't pad out empty cards on new tenants.
 */
export function DashboardTopSuppliersCard({
  topSuppliers,
}: {
  topSuppliers: DashboardTopSupplier[];
}): JSX.Element | null {
  const { t, i18n } = useTranslation();
  if (!topSuppliers || topSuppliers.length === 0) return null;
  const locale = i18n.language;
  const year = new Date().getFullYear();

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden />
            {t("dashboard.topSuppliers.title", "Top proveedores")}
          </CardTitle>
          <CardDescription>
            {t(
              "dashboard.topSuppliers.description",
              "Gasto {{year}}: comprobantes recibidos + gastos manuales.",
              { year },
            )}
          </CardDescription>
        </div>
        <Button asChild variant="ghost" size="sm" className="gap-1.5">
          <Link to="/suppliers">
            {t("dashboard.topSuppliers.viewAll", "Ver todos")}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-border">
          {topSuppliers.map((s) => {
            const amounts = supplierAmounts(s);
            return (
              <li key={s.supplierId}>
                <Link
                  to={`/suppliers/${s.supplierId}`}
                  className="flex items-center justify-between gap-4 rounded-md px-2 py-2.5 transition-colors hover:bg-muted/40"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">
                      {s.displayName?.trim() || s.legalName}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t("dashboard.topSuppliers.recordCount", {
                        count: s.recordsCount,
                      })}
                    </div>
                  </div>
                  <div className="shrink-0 space-y-0.5 text-right">
                    {amounts.length === 0 ? (
                      <span className="text-sm text-muted-foreground">—</span>
                    ) : (
                      amounts.map(({ currency, value }) => (
                        <div
                          key={currency}
                          className="flex items-baseline justify-end gap-1.5"
                          // A negative total is real: credit notes (102/112/122)
                          // subtract from the year's spend. Say so rather than
                          // leaving a bare minus sign to be read as a glitch.
                          title={
                            value < 0
                              ? t(
                                  "dashboard.topSuppliers.negativeHint",
                                  "Saldo negativo: las notas de crédito superan lo facturado este año.",
                                )
                              : undefined
                          }
                        >
                          <span className="text-xs text-muted-foreground">
                            {currency}
                          </span>
                          <span className="text-sm font-medium tabular-nums text-foreground">
                            {formatAmount(value, locale)}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
